'use client'

import React, { useMemo, useCallback } from 'react'
import { MobileHeader, HeaderDataMobile } from '@quark/header'
import { Button } from '@quark/button'
import { Text } from '@quark/typography'
import { Flex, Container } from '@quark/layout'
import { Avatar } from '@quark/avatar'
import { ArrowRight } from '@quark/icons'
import { logoutUser } from '@services/loginService'
import { KEY_REFRESH_TOKEN, KEY_USER_ID } from '@components/GlobalHeader/constants'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import Cookies from 'js-cookie'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { getModifiedOptions } from '@factories/getGlobalHeaderData'

interface HeaderMobileProps {
  isLoggedIn: boolean
  userData?: any
  content?: any
  animate?: boolean
}

const HeaderMobile: React.FC<HeaderMobileProps> = ({ isLoggedIn, userData, content, animate = false }) => {
  const config = useEnvVariablesClientConfig()
  const handleLogout = useCallback(async () => {
    try {
      await logoutUser({
        userId: Cookies.get(KEY_USER_ID),
        refreshToken: Cookies.get(KEY_REFRESH_TOKEN),
        env: config?.ACKO_ENV,
      })
    } catch (error) {
      console.error('Logout failed', error)
    } finally {
      Cookies.remove('refresh_token')
      Cookies.remove('user_id')
      window.location.reload()
    }
  }, [config])

  const handleNavClick = useCallback((url: string) => {
    void trackEvent('tap_header_raise_a_claim_cta', {
      page_type: 'seo',
      redirection_url: url,
    })

    if (url && url !== '') window.location.href = url
  }, [])

  const renderUserData = useMemo(
    () => (
      <Flex flexDirection="column" onClick={() => { window.open('/profile', '_blank') }}>
        <Flex flexDirection={{ sm: 'column', md: 'column', lg: 'row' }}>
          <Container
            backgroundColor="base/elevated"
            padding="spacing-20"
            borderRadius={{ sm: 'radius-3xl', md: 'radius-3xl', lg: 'radius-3xl' }}>
            <Flex alignItems="center" justifyContent="space-between" gap={{ sm: 'spacing-4', md: 'spacing-4' }}>
              <Flex
                alignItems="center"
                gap={{ sm: 'spacing-12', md: 'spacing-12', lg: 'spacing-12' }}
                justifyContent="center">
                <Avatar size={{ sm: 'lg', md: 'lg', lg: 'lg' }} backgroundColor="accent/Violet/Violet-100">
                  <Text
                    fontStyle={{
                      sm: 'um_text-xl/normal',
                      md: 'um_text-xl/normal',
                      lg: 'um_text-base/normal',
                    }}
                    color="base/primary"
                    lineHeight="line-height-0">
                    {userData?.avatar}
                  </Text>
                </Avatar>
                <Flex alignItems="baseline" flexDirection="column" gap="spacing-4">
                  <Text
                    as="div"
                    fontStyle={{
                      sm: 'um_text-base/normal',
                      md: 'um_text-base/normal',
                      lg: 'um_text-base/normal',
                    }}
                    color="base/primary">
                    {userData?.name}
                  </Text>
                  <Text
                    as="div"
                    fontStyle={{
                      sm: 'um_text-sm/medium',
                      md: 'um_text-sm/medium',
                      lg: 'um_text-sm/medium',
                    }}
                    color="base/secondary">
                    {userData?.phone}
                  </Text>
                </Flex>
              </Flex>
              <Text
                as="div"
                fontStyle={{
                  sm: 'um_text-2xl/normal',
                  md: 'um_text-2xl/normal',
                  lg: 'um_text-base/normal',
                }}
                color="base/primary"
                lineHeight="line-height-0">
                <ArrowRight />
              </Text>
            </Flex>
          </Container>
        </Flex>
      </Flex>
    ),
    [userData]
  )

  const renderFooter = useMemo(
    () =>
      !isLoggedIn || !userData ? (
        <Flex gap="spacing-20">
          <Button
            type="solid"
            size="block"
            onClick={() => {
              window.location.href = '/authn/v1/signin'
            }}>
            <Text fontStyle="um_text-base/normal" color="base/inverted primary">
              Login
            </Text>
          </Button>
        </Flex>
      ) : (
        <Flex gap="spacing-20">
          <Button
            type="solid"
            size="block"
            onClick={() => {
              void handleLogout()
            }}>
            <Text fontStyle="um_text-base/normal" color="base/inverted primary">
              Logout
            </Text>
          </Button>
        </Flex>
      ),
    [isLoggedIn, userData]
  )

  const headerData: HeaderDataMobile = useMemo(() => {
    const config: HeaderDataMobile = {
      logo: {
        darkUrl: content?.darkThemeLogoUrl,
        lightUrl: content?.lightThemeLogoUrl,
      },
      options: getModifiedOptions(content),
      navElements: [
        <Container
          key="file-claim-nav"
          paddingLeft="spacing-12"
          paddingRight="spacing-12"
          paddingTop="spacing-8"
          paddingBottom="spacing-8"
          backgroundColor="accent/Violet/Violet-100"
          borderRadius="radius-xl"
          onClick={() => handleNavClick('/myaccount')}>
          <Text fontStyle="um_text-base/normal" color="base/primary">
            File a claim
          </Text>
        </Container>,
      ],
    }

    if (userData && isLoggedIn) {
      config.userData = renderUserData
      config.footer = renderFooter
    } else {
      config.footer = renderFooter
    }

    return config
  }, [isLoggedIn, renderUserData, renderFooter])
  return <MobileHeader headerData={headerData} animate={animate} />
}

export default React.memo(HeaderMobile)
